import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { VideoModal } from "../deck/VideoModal";
import { useDeck } from "../../lib/deck-context";
import { fmtM, fmtPct } from "../../lib/model";
import { Eyebrow, Metric, Note, Reveal, Section, StatusBadge } from "../deck/primitives";

const Hero = ({ onWatch }) => (
  <div data-testid="overview-hero" className="relative h-[78vh] min-h-[560px] overflow-hidden bg-ink-deep">
    <motion.img
      src="/img/rendering.jpg"
      alt="Murrieta Family Commons conceptual rendering"
      className="absolute inset-0 h-full w-full object-cover"
      initial={{ scale: 1.06, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0F2235] via-[#0F2235]/55 to-[#0F2235]/10" />
    <div className="relative h-full flex flex-col justify-end px-6 sm:px-10 lg:px-16 pb-14 max-w-[1440px] mx-auto">
      <Reveal delay={0.3} className="flex flex-wrap items-center gap-3 mb-6">
        <span className="font-mono text-[11px] tracking-[0.4em] text-arq-mid">ARQITEK</span>
        <span className="h-px w-8 bg-white/30" />
        <span className="font-mono text-[11px] tracking-[0.22em] text-white/60 uppercase">01 · Investment Overview</span>
        <StatusBadge s="preliminary" />
      </Reveal>
      <Reveal delay={0.45}>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-[88px] text-white leading-[0.98] tracking-tight max-w-5xl">
          Murrieta Family Commons Project
        </h1>
      </Reveal>
      <Reveal delay={0.6}>
        <p className="mt-6 text-lg md:text-xl text-white/75 font-light">City of Murrieta Commercial Development Opportunity</p>
      </Reveal>
      <Reveal delay={0.75}>
        <button
          data-testid="hero-watch-video-btn"
          onClick={onWatch}
          className="group mt-9 inline-flex items-center gap-4 rounded-full bg-white/10 backdrop-blur-xl border border-white/25 pl-2 pr-7 py-2 text-white hover:bg-white/20 transition-colors duration-300"
        >
          <span className="h-11 w-11 rounded-full bg-arq grid place-items-center transition-transform duration-300 group-hover:scale-105">
            <Play size={17} className="ml-0.5" fill="currentColor" />
          </span>
          <span className="text-[15px] tracking-wide">Watch the Commons</span>
        </button>
      </Reveal>
    </div>
    <div className="hidden sm:block absolute top-6 right-6 sm:right-10 text-[10px] font-mono tracking-[0.18em] text-white/50">CONCEPTUAL RENDERING · NOT AN APPROVED SITE PLAN</div>
  </div>
);

export default function Overview() {
  const { m, inputs } = useDeck();
  const [video, setVideo] = useState(false);
  const cards = [
    { label: "Working Rentable Area", value: `${inputs.sf.toLocaleString()} SF`, sub: "Architect validation pending", status: "preliminary", id: "area" },
    { label: "Stabilized NOI", value: fmtM(m.noi, 3), sub: "Before final City ground rent", status: "preliminary", id: "noi" },
    { label: "Partner Base Cap Rate", value: fmtPct(inputs.cap), sub: "Aligned to Inland Empire Q3 2026", status: "preliminary", id: "cap", primary: true },
    { label: "Base Stabilized Value", value: fmtM(m.value), sub: "Income-based — not an appraisal", status: "preliminary", id: "value", primary: true },
    { label: "Preliminary Total Development Cost", value: fmtM(inputs.tdc), sub: `≈ $${Math.round(m.perSf)} per rentable SF all-in`, status: "preliminary", id: "tdc" },
    { label: "Build + Sell Gross Project Profit", value: fmtM(m.profit), sub: `${fmtPct(m.poc * 100, 1)} profit on cost · after ${inputs.saleCost}% sale cost`, status: "preliminary", id: "profit" },
  ];
  return (
    <div data-testid="section-overview">
      <Hero onWatch={() => setVideo(true)} />
      <VideoModal open={video} onOpenChange={setVideo} />
      <Section>
        <div className="grid lg:grid-cols-12 gap-12 mb-14">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Investment Thesis</Eyebrow>
            <div className="mt-4 h-px w-16 bg-arq" />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p data-testid="investment-thesis" className="font-display text-2xl md:text-[30px] leading-[1.35] text-ink">
              Arqitek has originated a City-owned downtown development opportunity currently advancing through City review. The strategy is to secure contractual development control, reduce project risk through entitlement, design and leasing, and preserve multiple monetization opportunities from development-rights assignment through long-term ownership.
            </p>
          </Reveal>
        </div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <Metric key={c.id} testid={`metric-card-${c.id}`} label={c.label} value={c.value} sub={c.sub} status={c.status} primary={c.primary} delay={0.05 * i} />
          ))}
        </div>
        <Note testid="overview-disclaimer" className="mt-8">
          <strong className="text-ink">Preliminary underwriting</strong> — subject to City terms, design, leasing, contractor pricing, financing and due diligence.
        </Note>
      </Section>
    </div>
  );
}
